import React, { useCallback, useRef, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  TextField,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import axios from 'axios'

import { ImageWidgetData, DashboardMedia } from '../../../types/gotypes'
import { API_BASE_URL } from '../../../constants/system'
import { useMediaUrl } from '../../../hooks/useMediaUrl'
import { DashboardMediaPicker } from './DashboardMediaPicker'
import { MediaLayoutControls, mediaAlignToFlex, MediaAlign } from './MediaLayoutControls'

interface ImageWidgetEditorProps {
  customer: string
  job: string
  dashboardId: string
  data: ImageWidgetData | undefined
  onDataChange: (data: ImageWidgetData) => void
}

export const ImageWidgetEditor: React.FC<ImageWidgetEditorProps> = ({
  customer,
  job,
  dashboardId,
  data,
  onDataChange,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Single-job dashboards (#961): the image always inherits the dashboard's
  // own job for upload and read-back — no per-widget job picker (#1069).
  const preview = useMediaUrl(customer, job, data?.file_path || '')

  const handleUpload = useCallback(
    async (file: File) => {
      setUploading(true)
      setUploadProgress(0)
      setUploadError(null)

      const formData = new FormData()
      formData.append('file', file)
      formData.append('job', job)

      try {
        const res = await axios.post(`${API_BASE_URL}/dashboards/${customer}/${dashboardId}/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) {
              setUploadProgress(Math.round((e.loaded * 100) / e.total))
            }
          },
        })

        const filePath = res.data.path as string
        onDataChange({
          ...data,
          job,
          file_path: filePath,
          alt_text: data?.alt_text,
        })
      } catch (err: unknown) {
        const message =
          axios.isAxiosError(err) && err.response?.data?.error
            ? err.response.data.error
            : err instanceof Error
              ? err.message
              : 'Upload failed'
        setUploadError(message)
      } finally {
        setUploading(false)
      }
    },
    [customer, job, dashboardId, data, onDataChange],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleUpload(file)
      // Reset input so re-selecting the same file triggers change
      e.target.value = ''
    },
    [handleUpload],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        handleUpload(file)
      }
    },
    [handleUpload],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleAltTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (data) {
        onDataChange({ ...data, alt_text: e.target.value })
      }
    },
    [data, onDataChange],
  )

  const handlePickerSelect = useCallback(
    (media: DashboardMedia) => {
      // Re-pointing at library media keeps alt text and layout choices; the
      // job is always the dashboard's own (#961/#1069).
      onDataChange({
        ...data,
        job,
        file_path: media.blob_path,
      })
    },
    [data, job, onDataChange],
  )

  const handleFitModeChange = useCallback(
    (fitMode: 'contain' | 'cover') => {
      if (data) {
        onDataChange({ ...data, fit_mode: fitMode })
      }
    },
    [data, onDataChange],
  )

  const handleAlignChange = useCallback(
    (align: MediaAlign) => {
      if (data) {
        onDataChange({ ...data, align })
      }
    },
    [data, onDataChange],
  )

  const hasExistingImage = Boolean(data?.file_path)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Button
        variant="outlined"
        color="info"
        size="small"
        onClick={() => setPickerOpen(true)}
        disabled={uploading}
        sx={{ alignSelf: 'flex-start' }}
      >
        Choose from Library
      </Button>

      <DashboardMediaPicker
        customer={customer}
        job={job}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
      />


      {uploadError && (
        <Alert severity="error" onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {hasExistingImage && preview.url ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
          <Box
            component="img"
            src={preview.url}
            alt={data?.alt_text || 'Current image'}
            sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {data?.file_path}
          </Typography>
          <Button
            variant="outlined"
            color="info"
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Replace Image
          </Button>
        </Box>
      ) : (
        <Paper
          variant="outlined"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            p: 4,
            textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer',
            border: '2px dashed',
            borderColor: 'grey.400',
            backgroundColor: 'grey.50',
            '&:hover': uploading ? {} : { borderColor: 'info.main', backgroundColor: 'action.hover' },
          }}
          onClick={() => {
            if (!uploading) fileInputRef.current?.click()
          }}
        >
          {uploading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Uploading... {uploadProgress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{ width: '100%' }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500' }} />
              <Typography variant="body2" color="text.secondary">
                Drop an image here or click to browse
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Accepts image files (PNG, JPG, GIF, etc.)
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {hasExistingImage && preview.loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileSelect}
      />

      {hasExistingImage && (
        <>
          <Box>
            <Typography variant="overline" color="text.secondary">Fit</Typography>
            <ToggleButtonGroup
              value={data?.fit_mode || 'contain'}
              exclusive
              onChange={(_e, val: 'contain' | 'cover' | null) => val && handleFitModeChange(val)}
              size="small"
            >
              <ToggleButton value="contain">Fit</ToggleButton>
              <ToggleButton value="cover">Fill</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <MediaLayoutControls
            align={(data?.align as MediaAlign) || 'center'}
            onAlignChange={handleAlignChange}
          />
        </>
      )}

      <TextField
        label="Alt Text (optional)"
        size="small"
        value={data?.alt_text || ''}
        onChange={handleAltTextChange}
        placeholder="Describe the image for accessibility"
        disabled={!hasExistingImage}
      />
    </Box>
  )
}

interface ImageWidgetRendererProps {
  data: ImageWidgetData
  customer: string
}

export const ImageWidgetRenderer: React.FC<ImageWidgetRendererProps> = ({ data, customer }) => {
  const { url, loading, error } = useMediaUrl(customer, data.job, data.file_path)

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (!url) {
    return (
      <Typography variant="body2" color="text.secondary">
        No image available
      </Typography>
    )
  }

  const fitMode = data.fit_mode === 'cover' ? 'cover' : 'contain'
  const align = mediaAlignToFlex(data.align)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', gap: 1 }}>
      {/* Row flex container: the user's left/center/right choice is the
          HORIZONTAL axis, i.e. justifyContent — not alignItems, which is
          vertical here and made the toggles look dead (2026-08-25). */}
      <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', justifyContent: align, alignItems: 'center' }}>
        <Box
          component="img"
          src={url}
          alt={data.alt_text || ''}
          sx={{
            objectFit: fitMode,
            maxWidth: '100%',
            maxHeight: '100%',
            width: fitMode === 'cover' ? '100%' : 'auto',
            height: fitMode === 'cover' ? '100%' : 'auto',
          }}
        />
      </Box>
      {data.alt_text && (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {data.alt_text}
        </Typography>
      )}
    </Box>
  )
}
